import {get} from 'object-path-immutable'
import React, {useCallback, useMemo, useState} from 'react'
import {getSampleWebhookEndpointPayload} from '../../common/sample-formats/webhooks'
import {
  AnalyticsEventType,
  CustomEventProperties,
  MapperCodec,
  WebhookRequestPayload
} from '../../common/types'
import {
  useUpdateWebhookEndpointMutation,
  WebhookEndpointDetailFragment
} from '../../data/workbench-api/generated/webhook-endpoint'
import {useEnvironment} from '../../environment'
import {useCustomProfileFields} from '../../hooks/use-custom-profile-fields'
import {useAnalytics} from '../../lib/analytics/analytics'
import {BigModal} from '../action-flow/components/big-modal'
import {
  Mapper,
  MappingEditor
} from '../action-flow/edit/steps/components/mapping-editor'

interface EditMappingModalProps {
  webhookEndpoint: WebhookEndpointDetailFragment
  eventConfigMappings?: Record<string, CustomEventProperties>
  handleClose: () => void
}

export const WebhookMappingModal = ({
  webhookEndpoint,
  eventConfigMappings,
  handleClose
}: EditMappingModalProps) => {
  return (
    <BigModal title="Edit mapping" onClose={handleClose}>
      <WebhookMappingModalContent
        webhookEndpoint={webhookEndpoint}
        eventConfigMappings={eventConfigMappings}
        handleClose={handleClose}
      />
    </BigModal>
  )
}

interface WebhookMappingModalContentProps {
  webhookEndpoint: WebhookEndpointDetailFragment
  handleClose: () => void
  eventConfigMappings?: Record<string, CustomEventProperties>
}
const WebhookMappingModalContent = ({
  webhookEndpoint,
  eventConfigMappings,
  handleClose
}: WebhookMappingModalContentProps) => {
  const analytics = useAnalytics()
  const {environment} = useEnvironment()
  const [updateMutation] = useUpdateWebhookEndpointMutation()

  const isBatched = !!webhookEndpoint.batchInterval

  const [payloadMapping, setPayloadMapping] = useState<Mapper>(() =>
    webhookEndpoint.payloadMapping &&
    MapperCodec.is(webhookEndpoint.payloadMapping)
      ? webhookEndpoint.payloadMapping
      : {
          type: 'script',
          script: ''
        }
  )

  const [customProfileFields] = useCustomProfileFields(
    webhookEndpoint.environmentId
  )

  const rawPayload: WebhookRequestPayload = useMemo(() => {
    const profileCustomFields = customProfileFields.reduce(
      (acc, {name}) => ({
        ...acc,
        [name]: `<${name}>`
      }),
      {}
    )
    return getSampleWebhookEndpointPayload({
      environmentId: webhookEndpoint.environmentId,
      types: webhookEndpoint.types?.map(String) as AnalyticsEventType[],
      cardName: get(
        webhookEndpoint.cardTemplateDefinition,
        'metadata.cardName',
        ''
      ),
      cardTemplateId: webhookEndpoint?.cardTemplateId ?? undefined,
      cardDefinition: webhookEndpoint?.cardTemplateDefinition ?? undefined,
      isBatched: !!webhookEndpoint.batchInterval,
      webhookId: webhookEndpoint.id,
      mappedEventConfigProperties: eventConfigMappings,
      includeUserProfile: profileCustomFields
    })
  }, [webhookEndpoint, eventConfigMappings, customProfileFields])

  const loadSampleScript = useCallback(
    () => (isBatched ? sampleScriptBatched : sampleScript),
    [isBatched]
  )

  return (
    <MappingEditor
      input={rawPayload}
      mapping={payloadMapping}
      onChangeMapping={setPayloadMapping}
      mode="request"
      loadSampleScript={loadSampleScript}
      onSavedChanges={mapping => {
        let saveMapping: Mapper | null = mapping

        if (
          (mapping.type === 'script' && mapping.script === '') ||
          Object.keys(mapping).length === 0
        ) {
          // remove the payload mapping if its empty
          saveMapping = null
        }

        updateMutation({
          variables: {
            id: webhookEndpoint.id,
            environmentId: webhookEndpoint.environmentId,
            payloadMapping: saveMapping
          },
          onCompleted: () => {
            handleClose()
            if (environment) {
              analytics.track({
                event: 'Edited configuration',
                properties: {
                  org_id: environment.organisationId,
                  environment_id: environment.id,
                  category: 'configuration>webhooks>subscriptions'
                }
              })
            }
          }
        })
      }}
    />
  )
}

const sampleScript = `({
  environmentId: environmentId,
  sentAt: data.timestamp,
  user: {
    id: data.endUserId
  }
})`

const sampleScriptBatched = `({
  environmentId: environmentId,
  events: data.map(event => ({
    sentAt: event.timestamp,
    user: {
      id: event.endUserId
    }
  }))
})`
